import * as React from 'react';
import { useEffect } from 'react';
import { useIsClientSide } from '../../../utils/hooks';
import { Cart, CartsDataActions } from '@msdyn365-commerce/retail-proxy';
import { QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { IRequestContext, ICoreContext } from '@msdyn365-commerce/core-internal';
import { IActionContext } from '@msdyn365-commerce/core';
import Modal from '../../../shared/components/modal.component';
import { clearCookie, getCookie, saveCookie, checkIfFunctionalConsentGiven, CART_COOKIE_NAME } from '../../../shared/functions/cookie';
import { normalizeUrl, areUrlsEqual } from '../../../shared/functions/url-helpers';

export interface ExtHeaderHelperConfiguration {
    enableRedirectOnCookie?: Boolean;
    cookieExpiration?: number;
    shouldRedirectToLocale?: Boolean;
    centerHeader?: Boolean;
    shouldLogOutInvalidLocaleUser?: Boolean;
    deleteCookiesOnLocaleSwitch?: Boolean;
    cartUpdateToggle?: Boolean;
    removeUserCartItems?: Boolean;
    shouldDisplayCartUpdate?: Boolean;
    cartUpdateMessage?: string;
    defaultButtonMessage?: string;
    cartLink?: string;
    cartLinkButtonMessage?: string;
    cart?: Readonly<Cart>;
    telemetry?: any;
    context: ICoreContext;
    useStickyHeader?: Boolean;
    appConfig: Record<string, any>;
}

interface CartLine {
    id?: string;
    productId?: number;
    barcode?: string;
    description?: string;
    quantity?: number;
}

export interface LocaleGenderUrls {
    locale: string;
    female: string;
    male: string;
}

export type GenderCookieValue = 'male' | 'female';

export interface ExtHeaderHelperProps {
    config: ExtHeaderHelperConfiguration;
    request: IRequestContext;
}
var savedNewItems: CartLine[] = [];
export const ExtHeaderHelper = ({ config, request }: ExtHeaderHelperProps) => {
    return (
        <>
            <CookieHandlerHelper config={config} request={request} />
            <LocaleRedirectHelper config={config} request={request} />
            <CartUpdateHelper config={config} request={request} />
        </>
    );
};

export const GENDER_COOKIE_NAME = 'bsGenderPage';
const GENERIC_FEMALE_URLS = ['ladies'];
const GENERIC_MALE_URLS = ['men', 'mens'];
const HEADER_LOGO_SELECTORS = ['.ms-header__logo', '.ms-header__mobile-logo'];

// TODO remove this and modify logo component in extended header so that it updates when link changes
const updateHeaderLogoLink = (hasConsented: boolean, baseUrl: string, genderPath?: string) => {
    HEADER_LOGO_SELECTORS.forEach(selector => {
        const anchorTag = document.querySelector(selector)?.querySelector('a');
        if (!anchorTag) {
            return;
        }

        if (hasConsented && genderPath) {
            const logoLinkWithGender = `${baseUrl}${genderPath}`;
            if (anchorTag.href !== logoLinkWithGender) {
                anchorTag.href = logoLinkWithGender;
            }
        } else if (anchorTag.href !== baseUrl) {
            anchorTag.href = baseUrl;
        }
    });
};

export const setGenderCookieIfAllowed = async (
    context: ICoreContext,
    cookieExpiration: number,
    cookieValue: GenderCookieValue | undefined,
    genderPath: string | undefined,
    baseUrl: string
) => {
    const functionalConsent = await checkIfFunctionalConsentGiven();
    if (functionalConsent === undefined) {
        return;
    }
    if (functionalConsent === false) {
        clearCookie(GENDER_COOKIE_NAME, baseUrl);
    }

    if (!genderPath || !cookieValue) {
        return;
    }

    if (functionalConsent) {
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() + cookieExpiration);
        const expirationDateInUtc = currentDate.toUTCString();

        // Necessary to set cookie like this, for server side components to have access to it
        context?.request.cookies.setConsentCookie();
        context?.request.cookies.set(GENDER_COOKIE_NAME, cookieValue, {
            expires: new Date(expirationDateInUtc),
            path: normalizeUrl(baseUrl)
        });
    }
    updateHeaderLogoLink(functionalConsent, baseUrl, genderPath);
};

const CookieHandlerHelper = ({ config, request }: ExtHeaderHelperProps) => {
    const isClient = useIsClientSide();
    const { enableRedirectOnCookie = false, cookieExpiration = 0, appConfig, context } = config;
    useEffect(() => {
        const localeGenderUrls: LocaleGenderUrls[] | undefined = appConfig.localeGenderUrls;
        if (!isClient || !context || !localeGenderUrls) {
            return;
        }

        const genderUrlsForCurrentLocale = localeGenderUrls.find(genderUrls => {
            return genderUrls.locale === context.request.locale;
        });
        if (!genderUrlsForCurrentLocale) {
            return;
        }

        const baseUrl: string = request.app.routes.home.destinationUrl;
        const { pathname } = window.location;
        if (enableRedirectOnCookie && areUrlsEqual(baseUrl, pathname)) {
            // If cookie exists, redirect to the page ONLY for male
            const genderCookieValue = getCookie(GENDER_COOKIE_NAME) as GenderCookieValue;
            if (genderCookieValue === 'male') {
                window.location.replace(`${baseUrl}${genderUrlsForCurrentLocale.male}`);
            }
        } else {
            //Set cookie
            let urlPathForCurrentGender: string | undefined;
            let cookieValue: GenderCookieValue | undefined;
            const subPaths = pathname.split('/');
            const femaleUrlPathsToLookFor = [genderUrlsForCurrentLocale.female, ...GENERIC_FEMALE_URLS];

            const currentPathHasFemaleUrl = subPaths.some(subPath => {
                return femaleUrlPathsToLookFor.includes(subPath);
            });
            if (currentPathHasFemaleUrl) {
                urlPathForCurrentGender = genderUrlsForCurrentLocale?.female;
                cookieValue = 'female';
            } else {
                const maleUrlPathsToLookFor = [genderUrlsForCurrentLocale.male, ...GENERIC_MALE_URLS];
                const currentPathHasMaleUrl = subPaths.some(subPath => {
                    return maleUrlPathsToLookFor.includes(subPath);
                });
                if (currentPathHasMaleUrl) {
                    urlPathForCurrentGender = genderUrlsForCurrentLocale.male;
                    cookieValue = 'male';
                }
            }

            const handleUpdateCookie = () => {
                setGenderCookieIfAllowed(context, cookieExpiration, cookieValue, urlPathForCurrentGender, baseUrl);
            };
            handleUpdateCookie();

            window.addEventListener('ucEvent', handleUpdateCookie);
            return () => {
                window.removeEventListener('ucEvent', handleUpdateCookie);
            };
        }
        return;
    }, [isClient]);
    return <></>;
};

const LocaleRedirectHelper = ({ config, request }: ExtHeaderHelperProps) => {
    const isClient = useIsClientSide();
    const { shouldRedirectToLocale = false, shouldLogOutInvalidLocaleUser = false, cookieExpiration = 0 } = config;
    useEffect(() => {
        //Clause guards
        if (!isClient) {
            return;
        }
        const cookieName = '_msdyn365___site_';

        if (shouldRedirectToLocale) {
            const cookieContent = getCookie(cookieName);
            if (cookieContent !== '' && window.location.pathname === '/') {
                window.location.replace(cookieContent);
            }
        } else {
            //If it should not redirect to locale, then we need to save the locale in a cookie
            var previousCookie = getCookie(cookieName);
            var path = window.location.pathname;
            const subPaths = path.split('/').filter(pathname => pathname !== '');
            if (subPaths.length > 0) {
                path = `/${subPaths[0]}`;
            }

            const currentDate = new Date();
            currentDate.setDate(currentDate.getDate() + cookieExpiration);
            const expirationDateInUtc = currentDate.toUTCString();
            clearCookie(cookieName);
            document.cookie = `${cookieName}=${path}; expires=${expirationDateInUtc}; path=/;`;
            //If the user is logged in on an invalid locale
            if (previousCookie && previousCookie !== path) {
                if (config.deleteCookiesOnLocaleSwitch) {
                    //Delete all cookies so they can be set again for the new locale
                    const allCookies = document.cookie.split(';');
                    for (let i = 0; i < allCookies.length; i++) {
                        const cookie = allCookies[i];
                        const [name] = cookie.split('=');
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
                    }
                }
                if (shouldLogOutInvalidLocaleUser && request.user.isAuthenticated) {
                    if (config.telemetry) {
                        //TODO: Should be information telemetry
                        config.telemetry.warning('User logged in on invalid locale. Logging out user.');
                    }
                    window.location.href = request.user.signOutUrl ?? '';
                }
            }
        }
    }, [isClient]);
    //TODO: Also run on isConsentGiven change?
    return <></>;
};

const CartUpdateHelper = ({ config, request }: ExtHeaderHelperProps): JSX.Element => {
    const [isModalOpen, setModalOpen] = React.useState(true);

    const toggleModal = () => {
        setModalOpen(!isModalOpen);
    };
    const isClient = useIsClientSide();
    const { cookieExpiration = 0 } = config;
    //Clause guards
    if (!isClient || !config.cartUpdateToggle) {
        return <></>;
    }
    var popup: JSX.Element = <></>;
    const cookieName = CART_COOKIE_NAME;
    const newCart: CartLine[] | undefined = config.cart?.CartLines?.map(item => {
        return {
            id: item.LineId,
            productId: item.ProductId,
            barcode: item.Barcode,
            description: item.Description,
            quantity: item.Quantity
        };
    });

    //if this has run and identified new items previously, skip the check
    if (savedNewItems && savedNewItems.length > 0) {
        saveCookie(cookieName, newCart, cookieExpiration);
        if (config.shouldDisplayCartUpdate) {
            const defaultButtonAction = config.removeUserCartItems
                ? () => removeCartLines(savedNewItems, config.context.actionContext, config.cart?.Id, config.telemetry)
                : undefined;
            return (
                <Modal
                    isOpen={isModalOpen}
                    toggle={toggleModal}
                    content={<p>{config.cartUpdateMessage}</p>}
                    defaultButtonAction={defaultButtonAction}
                    defaultButtonMessage={config.defaultButtonMessage}
                    buttons={
                        <button className='redirect-btn' onClick={() => redirect(config)}>
                            {config.cartLinkButtonMessage}
                        </button>
                    }
                ></Modal>
            );
        } else {
            if (config.removeUserCartItems) {
                removeCartLines(savedNewItems, config.context.actionContext, config.cart?.Id, config.telemetry);
            }
            return <></>;
        }
    }

    if (getCookie(cookieName) && request.user.isAuthenticated) {
        var newItems: any[] = [];
        var blackList: string[] = []; //as there can be multiple lines with the same barcode and quantity we need to keep track of the ones we have already "used"
        const cookie = getCookie(cookieName);
        const previousCart: CartLine[] | undefined = cookie ? JSON.parse(cookie) : undefined;
        //if the previous cart is empty, we don't remove new items
        if (previousCart && previousCart.length > 0) {
            newCart?.forEach(item => {
                var prevItem: CartLine | undefined = previousCart.find(
                    (prevItem: CartLine) =>
                        prevItem.barcode === item.barcode &&
                        prevItem.quantity === item.quantity &&
                        blackList.indexOf(prevItem.id ?? '') === -1
                );
                if (!prevItem) {
                    newItems.push(item);
                } else {
                    blackList.push(prevItem.id ?? '');
                }
            });
        }
        if (newItems && newItems.length > 0) {
            savedNewItems = newItems;
            config.telemetry.warning('_cart-update Cart change popup displayed');
            if (config.shouldDisplayCartUpdate) {
                const defaultButtonAction = config.removeUserCartItems
                    ? () => removeCartLines(savedNewItems, config.context.actionContext, config.cart?.Id, config.telemetry)
                    : undefined;
                popup = (
                    <Modal
                        isOpen={isModalOpen}
                        toggle={toggleModal}
                        content={<p>{config.cartUpdateMessage}</p>}
                        defaultButtonAction={defaultButtonAction}
                        defaultButtonMessage={config.defaultButtonMessage}
                        buttons={
                            <button className='redirect-btn' onClick={() => redirect(config)}>
                                {config.cartLinkButtonMessage}
                            </button>
                        }
                    ></Modal>
                );
            } else {
                if (config.removeUserCartItems) {
                    removeCartLines(savedNewItems, config.context.actionContext, config.cart?.Id, config.telemetry);
                }
            }
        }
    }
    saveCookie(cookieName, newCart, cookieExpiration);
    return popup;
};

export const saveCartCookie = (cookieName: string, data: any, cookieExpiration = 30) => {
    const newCart: CartLine[] | undefined = data?.map((item: any) => {
        return {
            id: item.LineId,
            productId: item.ProductId,
            barcode: item.Barcode,
            description: item.Description,
            quantity: item.Quantity
        };
    });
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + cookieExpiration);
    const expirationDateInUtc = currentDate.toUTCString();
    clearCookie(cookieName);
    document.cookie = `${cookieName}=${JSON.stringify(newCart)}; expires=${expirationDateInUtc}; path=/;`;
};

const removeCartLines = (cartLines?: CartLine[], context?: IActionContext, id?: string, telemetry?: any) => {
    var cartLineIds = cartLines?.map((cartLine: CartLine) => cartLine.id ?? '');
    if (cartLineIds && context && id) {
        CartsDataActions.removeCartLinesAsync(
            { callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) },
            id,
            cartLineIds
        )
            .then(() => {
                //reload the page to update the cart
                telemetry.warning('_cart-update Cart change popup removing cart lines');
                window.location.reload();
            })
            .catch(error => {
                telemetry.exception(error);
            });
    }
};

const redirect = (config: ExtHeaderHelperConfiguration) => {
    config.telemetry.warning('_cart-update Cart change popup redirecting to cart');
    window.location.href = config.cartLink ?? '';
};
